import axios, { AxiosInstance } from 'axios';
import { 
  AccountTask, 
  WorkerConfig, 
  ListAccountTasksResponse, 
  ApiResponse,
  ExecutionStats,
  ExecutionLog,
  WorkerInfo,
  WorkerConfigUpdate,
  WorkerPoolStatus,
  WorkerRequestResult,
  SeedingAccount,
  SeedingRole,
  BrandCharacter,
} from '../types';
import { authService } from '../services/auth';

// Prefer an explicit backend base URL in local dev, but keep the same-origin
// proxy route as the default for production/Page Functions.
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_API_URL || '/api/v1';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 10000,
    });

    // Add request interceptor for auth token
    this.client.interceptors.request.use(
      (config) => {
        const token = authService.getSessionToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        // Add business context header (required by JWTAuth when JWT has no embedded business_id)
        const businessId = authService.getBusinessId();
        if (businessId) {
          config.headers['X-Business-ID'] = businessId;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Add response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = error.response?.status;
        const config = error.config as any;

        if (status === 401 && config && !config._retry) {
          config._retry = true;

          // If embedded in biztada-app iframe, ask the shell to refresh the token
          if (window.parent && window.parent !== window) {
            window.parent.postMessage({ type: 'BIZTADA_REQUIRE_AUTH_REFRESH' }, '*');
            
            try {
              const newToken = await authService.waitForNewToken(10000); // Wait up to 10s
              if (newToken) {
                config.headers = config.headers || {};
                config.headers.Authorization = `Bearer ${newToken}`;
                return this.client.request(config); // Retry the original request
              }
            } catch (e) {
              console.error('Failed to wait for new token', e);
            }
          }

          // Fallback if not in iframe or refresh failed
          authService.logout();
          window.Telegram?.WebApp?.showAlert('Session expired. Please restart the app.');
        } else if (status === 400) {
          const errorCode = error.response.data?.code;
          if (errorCode === 'INVALID_CONTEXT' || errorCode === 'BUSINESS_ID_REQUIRED') {
            // Missing business context - redirect to business selection or show alert
            window.Telegram?.WebApp?.showAlert('Please select a business first');
            // If running in iframe/module, post message to parent
            window.parent?.postMessage({ type: 'BUSINESS_REQUIRED' }, '*');
          }
        }
        
        // Extract backend error message if available
        if (error.response?.data) {
          const backendError = error.response.data.error || error.response.data.message;
          const backendDetails = error.response.data.details;
          
          if (backendError) {
            // Create a more informative error
            const enhancedError = new Error(backendError);
            (enhancedError as any).details = backendDetails;
            (enhancedError as any).code = error.response.data.code;
            (enhancedError as any).status = error.response.status;
            return Promise.reject(enhancedError);
          }
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Worker Config Endpoints
  async getWorkerConfig(workerUuid: string): Promise<WorkerConfig> {
    const response = await this.client.get<ApiResponse<WorkerConfig>>(`/configs/${workerUuid}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get worker config');
    }
    return response.data.data!;
  }

  // Account Task Endpoints
  async listAccountTasks(workerUuid: string): Promise<AccountTask[]> {
    const response = await this.client.get<ApiResponse<ListAccountTasksResponse>>(
      `/configs/${workerUuid}/account-tasks`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to list account tasks');
    }
    return response.data.data?.account_tasks || [];
  }

  async getAccountTask(workerUuid: string, accountId: string): Promise<AccountTask> {
    const response = await this.client.get<ApiResponse<AccountTask>>(
      `/configs/${workerUuid}/account-tasks/${accountId}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get account task');
    }
    return response.data.data!;
  }

  async addAccountTask(workerUuid: string, accountTask: AccountTask): Promise<AccountTask> {
    const response = await this.client.post<ApiResponse<AccountTask>>(
      `/configs/${workerUuid}/account-tasks`,
      accountTask
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to add account task');
    }
    return response.data.data!;
  }

  async updateAccountTask(
    workerUuid: string,
    accountId: string,
    accountTask: Partial<AccountTask>
  ): Promise<AccountTask> {
    const response = await this.client.put<ApiResponse<AccountTask>>(
      `/configs/${workerUuid}/account-tasks/${accountId}`,
      accountTask
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update account task');
    }
    return response.data.data!;
  }

  async deleteAccountTask(workerUuid: string, accountId: string): Promise<void> {
    const response = await this.client.delete<ApiResponse<void>>(
      `/configs/${workerUuid}/account-tasks/${accountId}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete account task');
    }
  }

  // Execution Stats Endpoints
  async getExecutionStats(workerUuid: string): Promise<ExecutionStats> {
    const response = await this.client.get<ApiResponse<ExecutionStats>>(
      `/execution-logs/${workerUuid}/stats`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get execution stats');
    }
    return response.data.data!;
  }

  async getExecutionLogs(workerUuid: string, limit: number = 10): Promise<ExecutionLog[]> {
    const response = await this.client.get<ApiResponse<ExecutionLog[]>>(
      `/execution-logs/${workerUuid}`,
      { params: { limit } }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get execution logs');
    }
    return response.data.data || [];
  }

  // Worker Config Management
  async getMyWorkers(): Promise<WorkerInfo[]> {
    const response = await this.client.get<ApiResponse<WorkerInfo[]>>('/configs/my-workers');
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get workers');
    }
    return response.data.data || [];
  }

  async getWorkerPoolStatus(): Promise<WorkerPoolStatus> {
    const response = await this.client.get<ApiResponse<WorkerPoolStatus>>('/workers/pool-status');
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get worker pool status');
    }
    return response.data.data!;
  }

  async requestAdditionalWorker(): Promise<WorkerRequestResult> {
    const response = await this.client.post<ApiResponse<WorkerRequestResult>>('/workers/request');
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to request worker');
    }
    return response.data.data!;
  }

  async updateWorkerConfig(
    workerUuid: string,
    config: Partial<WorkerConfigUpdate>
  ): Promise<WorkerConfig> {
    const response = await this.client.put<ApiResponse<WorkerConfig>>(
      `/configs/${workerUuid}`,
      config
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update config');
    }
    return response.data.data!;
  }

  // 🆕 Seeding Account Management
  async getBrandCharacters(): Promise<BrandCharacter[]> {
    const response = await this.client.get<ApiResponse<BrandCharacter[]>>('/brand-characters');
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get brand characters');
    }
    return response.data.data || [];
  }

  async getMyAccounts(): Promise<SeedingAccount[]> {
    const response = await this.client.get<ApiResponse<{accounts: SeedingAccount[], total: number}>>('/my-accounts');
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get accounts');
    }
    // Backend returns data.data.accounts
    const accounts = response.data.data?.accounts;
    // Ensure it's always an array
    return Array.isArray(accounts) ? accounts : [];
  }

  async createAccount(account: Partial<SeedingAccount>): Promise<SeedingAccount> {
    const response = await this.client.post<ApiResponse<SeedingAccount>>('/accounts', account);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to create account');
    }
    return response.data.data!;
  }

  async updateAccount(accountId: string, updates: Partial<SeedingAccount>): Promise<void> {
    const response = await this.client.put<ApiResponse<void>>(`/accounts/${accountId}`, updates);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to update account');
    }
  }

  async deleteAccount(accountId: string): Promise<void> {
    const response = await this.client.delete<ApiResponse<void>>(`/accounts/${accountId}`);
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to delete account');
    }
  }

  async getAccountsByWorker(workerUuid: string): Promise<SeedingAccount[]> {
    const response = await this.client.get<ApiResponse<{accounts: SeedingAccount[], total: number}>>(
      `/accounts?worker_uuid=${workerUuid}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Failed to get accounts by worker');
    }
    return response.data.data?.accounts || [];
  }

  // 🆕 Worker Info
  async getWorkerInfo(workerUuid?: string): Promise<WorkerInfo | null> {
    try {
      const response = await this.client.get<ApiResponse<{workers: WorkerInfo[]}>>('/my-workers');
      if (!response.data.success) {
        return null;
      }
      const workers = response.data.data?.workers || [];
      if (workerUuid) {
        return workers.find(w => w.worker_uuid === workerUuid) || null;
      }
      return workers.length > 0 ? workers[0] : null;
    } catch (error) {
      console.error('Failed to get worker info:', error);
      return null;
    }
  }

  // Workflow list (lightweight — for campaign workflow picker)
  async getWorkflowList(): Promise<{ _id: string; name: string; status: string }[]> {
    const response = await this.client.get('/workflows');
    const list = response.data?.workflows || response.data || [];
    return list;
  }

  // 🆕 Campaign Endpoints
  async getCampaigns(): Promise<any[]> {
    const response = await this.client.get('/campaigns');
    return response.data.campaigns || [];
  }

  async createCampaign(campaign: any): Promise<any> {
    const response = await this.client.post('/campaigns', campaign);
    return response.data;
  }

  async updateCampaign(campaignId: string, campaign: any): Promise<any> {
    const response = await this.client.put(`/campaigns/${campaignId}`, campaign);
    return response.data;
  }

  async deleteCampaign(campaignId: string): Promise<void> {
    await this.client.delete(`/campaigns/${campaignId}`);
  }

  async updateCampaignWorkflowOrder(campaignId: string, workflowOrder: any[]): Promise<void> {
    await this.client.put(`/campaigns/${campaignId}/workflow-order`, { workflow_order: workflowOrder });
  }

  async activateCampaign(campaignId: string): Promise<any> {
    const response = await this.client.post(`/campaigns/${campaignId}/activate`, {});
    return response.data;
  }

  async deactivateCampaign(campaignId: string): Promise<any> {
    const response = await this.client.post(`/campaigns/${campaignId}/deactivate`, {});
    return response.data;
  }

  async runNowCampaign(campaignId: string): Promise<any> {
    const response = await this.client.post(`/campaigns/${campaignId}/run-now`, {});
    return response.data;
  }

  // 🆕 Node Registry Endpoints
  async getRegistryNodes(): Promise<any> {
    const response = await this.client.get('/workflows/node-registry');
    return response.data;
  }

  async getNodeInfo(nodeType: string): Promise<any> {
    const response = await this.client.get(`/workflows/node-registry/${nodeType}`);
    return response.data;
  }

  async getNodeCategories(): Promise<string[]> {
    const response = await this.client.get('/workflows/node-registry/categories');
    return response.data.categories || [];
  }

  async validateNodeConfig(nodeType: string, config: any): Promise<any> {
    const response = await this.client.post(`/workflows/node-registry/validate`, { 
      node_type: nodeType,
      config 
    });
    return response.data;
  }

  // 🆕 TikTok QR Login — polls latest QR captured by the worker
  async getTikTokQr(accountId: string): Promise<{ qr_base64: string; expires_at: number } | null> {
    try {
      const response = await this.client.get<{
        qr_base64: string;
        expires_at: number;
      }>(`/accounts/${accountId}/tiktok-qr`);
      return response.data;
    } catch {
      return null;
    }
  }

  // 🆕 Seeding Roles Library
  async getRoles(): Promise<SeedingRole[]> {
    const response = await this.client.get<{roles: SeedingRole[], version: string, last_updated: string}>('/library/seeding-roles');
    return response.data.roles || [];
  }
}

export const apiClient = new ApiClient();
export default apiClient;
